<template>
  <div
    class="min-h-screen bg-slate-50/70 pb-28 text-slate-800 font-sans antialiased"
  >
    <main
      class="mx-auto max-w-7xl px-3 sm:px-6 lg:px-8 py-6 sm:py-8 space-y-6 sm:space-y-8"
    >
      <!-- Loading Skeleton -->
      <div
        v-if="isLoading"
        class="space-y-6"
      >
        <div
          class="h-40 w-full animate-pulse rounded-2xl bg-slate-200/80"
        ></div>
        <div
          class="h-96 w-full animate-pulse rounded-2xl bg-slate-200/80"
        ></div>
      </div>

      <div
        v-else
        class="space-y-6 sm:space-y-8"
      >
        <!-- 1. LIVE TICKER & BULLETIN BOARD PREVIEW SECTION (Hidden on mobile < md) -->
        <section class="hidden md:block space-y-4">
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-2">
              <span
                class="h-2 w-2 rounded-full bg-emerald-500 animate-pulse"
              ></span>
              <h3
                class="text-xs font-bold uppercase tracking-wider text-slate-500"
              >
                Live Ticker & Bulletin Board Preview
              </h3>
            </div>
            <span
              class="rounded-md bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-700 shadow-xs"
            >
              Realtime Preview
            </span>
          </div>

          <!-- A. Ticker Rail Simulation -->
          <div
            class="overflow-hidden rounded-2xl border border-slate-800 bg-[#16554C] text-white shadow-lg"
          >
            <div
              class="flex items-center border-b border-white/10 bg-[#12423e] px-4 py-2 text-xs"
            >
              <div
                class="flex items-center gap-2 font-mono font-bold text-[#C99A3B] uppercase tracking-wider"
              >
                <BellRingIcon class="h-3.5 w-3.5" />
                <span>Notice Rail Ticker</span>
              </div>
            </div>
            <div
              class="p-3 bg-[#16554C] overflow-x-auto no-scrollbar flex items-center gap-6 text-xs font-mono"
            >
              <div
                v-for="(notice, idx) in activeTickerNotices"
                :key="idx"
                class="flex items-center gap-2 whitespace-nowrap shrink-0"
              >
                <i
                  :class="[
                    'fas',
                    notice.icon || 'fa-newspaper',
                    'text-[#C99A3B]',
                  ]"
                ></i>
                <span class="font-medium">{{
                  notice.title || "Untitled Notice"
                }}</span>
                <span
                  v-if="idx < activeTickerNotices.length - 1"
                  class="text-white/30 ml-4"
                  >•</span
                >
              </div>
              <span
                v-if="activeTickerNotices.length === 0"
                class="text-xs text-white/50 italic"
              >
                No active notices enabled for ticker bar.
              </span>
            </div>
          </div>

          <!-- B. Live Bulletin Board Preview (Polaroid Pin Cards) -->
          <div
            class="rounded-2xl border border-slate-200 bg-[#EFE8D6] p-4 sm:p-6 space-y-3 shadow-inner"
          >
            <div
              class="flex items-center justify-between border-b border-slate-300/70 pb-2"
            >
              <div class="flex items-center gap-2">
                <PinIcon class="h-4 w-4 text-amber-700" />
                <h4
                  class="text-xs font-bold uppercase tracking-wider text-slate-700"
                >
                  Bulletin Board Pinned Live Cards ({{
                    pinnedBulletins.length
                  }})
                </h4>
              </div>
              <span class="text-[10px] font-mono text-slate-500"
                >Matches Website Render</span
              >
            </div>

            <div
              v-if="pinnedBulletins.length > 0"
              class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 pt-2"
            >
              <div
                v-for="(card, pIdx) in pinnedBulletins"
                :key="card._id || pIdx"
                class="bg-white p-3 rounded-sm shadow-md relative flex flex-col justify-between transition transform hover:-translate-y-1 hover:rotate-0"
                :style="{
                  transform: `rotate(${pIdx % 2 === 0 ? '-1.5deg' : '1.5deg'})`,
                }"
              >
                <!-- Pin Graphic -->
                <div
                  class="absolute -top-2 left-1/2 -translate-x-1/2 h-3.5 w-3.5 rounded-full bg-rose-600 shadow-md ring-2 ring-rose-300 z-10"
                ></div>

                <div>
                  <div
                    class="w-full h-32 bg-slate-100 rounded-xs overflow-hidden relative"
                  >
                    <img
                      v-if="getImagePreview(card)"
                      :src="getImagePreview(card)"
                      :alt="card.title"
                      class="w-full h-full object-cover"
                    />
                    <div
                      v-else
                      class="w-full h-full flex flex-col items-center justify-center text-slate-400 bg-slate-200/60 p-2 text-center"
                    >
                      <ImageIcon class="h-6 w-6 mb-1" />
                      <span class="text-[9px] uppercase font-mono"
                        >No Image Uploaded</span
                      >
                    </div>
                  </div>

                  <h5
                    class="font-bold text-slate-900 text-xs mt-2.5 line-clamp-2 leading-snug"
                  >
                    {{ card.title || "Untitled Announcement" }}
                  </h5>
                  <p
                    class="text-[11px] text-slate-600 mt-1 line-clamp-2 leading-tight"
                  >
                    {{ card.description || "No description provided." }}
                  </p>
                </div>

                <div
                  class="mt-3 pt-2 border-t border-slate-100 flex items-center justify-between"
                >
                  <span
                    class="inline-flex items-center gap-1 text-[10px] font-bold text-teal-800 bg-teal-50 px-2 py-0.5 rounded-xs uppercase tracking-wider font-mono"
                  >
                    <i :class="['fas', card.icon || 'fa-newspaper']"></i>
                    {{ card.category || "General" }}
                  </span>
                  <span
                    v-if="card.isUrgent"
                    class="text-[9px] font-bold text-rose-600 bg-rose-50 px-1.5 py-0.5 rounded-xs uppercase"
                  >
                    Urgent
                  </span>
                </div>
              </div>
            </div>

            <div
              v-else
              class="py-8 text-center text-slate-500 italic text-xs"
            >
              No bulletins pinned. Toggle "Pin" on any announcement below to
              display it on the main Bulletin Board.
            </div>
          </div>
        </section>

        <!-- 2. STATS OVERVIEW CARDS -->
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div
            class="rounded-2xl border border-slate-200 bg-white p-4 shadow-xs flex items-center gap-4"
          >
            <div
              class="flex h-12 w-12 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600 font-bold shrink-0"
            >
              <FileTextIcon class="h-6 w-6" />
            </div>
            <div>
              <p
                class="text-xs font-semibold text-slate-500 uppercase tracking-wider"
              >
                Total Notices
              </p>
              <h4 class="text-xl font-bold text-slate-900">
                {{ noticeList.length }}
              </h4>
            </div>
          </div>

          <div
            class="rounded-2xl border border-slate-200 bg-white p-4 shadow-xs flex items-center gap-4"
          >
            <div
              class="flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600 font-bold shrink-0"
            >
              <CheckCircleIcon class="h-6 w-6" />
            </div>
            <div>
              <p
                class="text-xs font-semibold text-slate-500 uppercase tracking-wider"
              >
                Active Published
              </p>
              <h4 class="text-xl font-bold text-slate-900">
                {{ activeNoticesCount }}
              </h4>
            </div>
          </div>

          <div
            class="rounded-2xl border border-slate-200 bg-white p-4 shadow-xs flex items-center gap-4"
          >
            <div
              class="flex h-12 w-12 items-center justify-center rounded-xl bg-amber-50 text-amber-600 font-bold shrink-0"
            >
              <PinIcon class="h-6 w-6" />
            </div>
            <div>
              <p
                class="text-xs font-semibold text-slate-500 uppercase tracking-wider"
              >
                Pinned Bulletin
              </p>
              <h4 class="text-xl font-bold text-slate-900">
                {{ pinnedNoticesCount }}
              </h4>
            </div>
          </div>

          <div
            class="rounded-2xl border border-slate-200 bg-white p-4 shadow-xs flex items-center gap-4"
          >
            <div
              class="flex h-12 w-12 items-center justify-center rounded-xl bg-rose-50 text-rose-600 font-bold shrink-0"
            >
              <FlameIcon class="h-6 w-6" />
            </div>
            <div>
              <p
                class="text-xs font-semibold text-slate-500 uppercase tracking-wider"
              >
                Urgent Alerts
              </p>
              <h4 class="text-xl font-bold text-slate-900">
                {{ urgentNoticesCount }}
              </h4>
            </div>
          </div>
        </div>

        <!-- 3. NOTICE LIST MANAGER -->
        <section
          class="rounded-2xl border border-slate-200/80 bg-white p-4 sm:p-6 shadow-xs space-y-4"
        >
          <div
            class="flex flex-col sm:flex-row sm:items-center justify-between border-b border-slate-100 pb-4 gap-3"
          >
            <div>
              <div class="flex items-center space-x-2.5">
                <ListFilterIcon class="h-5 w-5 text-indigo-600" />
                <h2 class="text-base font-bold text-slate-900">
                  Notice Directory ({{ noticeList.length }})
                </h2>
              </div>
              <p class="text-xs text-slate-500 mt-0.5">
                Manage, pin, upload custom banner images, or select icons
              </p>
            </div>

            <button
              type="button"
              class="inline-flex items-center justify-center space-x-1.5 rounded-xl bg-indigo-600 px-4 py-2.5 text-xs font-bold text-white transition hover:bg-indigo-500 active:scale-95 cursor-pointer shadow-md shadow-indigo-600/20"
              @click="addNotice"
            >
              <PlusIcon class="h-4 w-4" />
              <span>New Announcement</span>
            </button>
          </div>

          <!-- NOTICE LIST ITEMS -->
          <div class="space-y-4">
            <div
              v-for="(notice, index) in noticeList"
              :key="notice._id || index"
              class="rounded-xl border transition-all duration-200 p-3.5 sm:p-4 space-y-4"
              :class="[
                notice.active !== false
                  ? 'border-slate-200 bg-slate-50/50 shadow-xs'
                  : 'border-slate-200/60 bg-slate-100/50 opacity-60',
                notice.isUrgent
                  ? 'ring-2 ring-rose-500/20 border-rose-200'
                  : '',
              ]"
            >
              <!-- TOP CONTROL STRIP -->
              <div
                class="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200/60 pb-2.5"
              >
                <div class="flex flex-wrap items-center gap-2">
                  <span class="text-xs font-mono font-bold text-slate-400"
                    >#{{ index + 1 }}</span
                  >
                  <span
                    class="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md"
                    :class="
                      notice.active !== false
                        ? 'bg-emerald-100 text-emerald-800'
                        : 'bg-slate-200 text-slate-600'
                    "
                  >
                    {{ notice.active !== false ? "Published" : "Draft" }}
                  </span>
                  <span
                    v-if="notice.isUrgent"
                    class="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md bg-rose-100 text-rose-800"
                  >
                    Urgent Alert
                  </span>
                  <span
                    v-if="notice.pinned"
                    class="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md bg-amber-100 text-amber-800 inline-flex items-center gap-1"
                  >
                    <PinIcon class="h-3 w-3" /> Pinned
                  </span>
                </div>

                <!-- SWITCHERS AND ACTIONS -->
                <div class="flex flex-wrap items-center gap-3">
                  <!-- Active Toggle -->
                  <label
                    class="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 cursor-pointer"
                  >
                    <span>Active:</span>
                    <button
                      type="button"
                      class="relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none"
                      :class="
                        notice.active !== false
                          ? 'bg-emerald-600'
                          : 'bg-slate-300'
                      "
                      @click="notice.active = notice.active === false"
                    >
                      <span
                        class="pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow-md ring-0 transition duration-200 ease-in-out"
                        :class="
                          notice.active !== false
                            ? 'translate-x-4'
                            : 'translate-x-0'
                        "
                      ></span>
                    </button>
                  </label>

                  <!-- Pin Toggle -->
                  <label
                    class="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 cursor-pointer"
                  >
                    <span>Pin:</span>
                    <button
                      type="button"
                      class="relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none"
                      :class="notice.pinned ? 'bg-amber-500' : 'bg-slate-300'"
                      @click="notice.pinned = !notice.pinned"
                    >
                      <span
                        class="pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow-md ring-0 transition duration-200 ease-in-out"
                        :class="
                          notice.pinned ? 'translate-x-4' : 'translate-x-0'
                        "
                      ></span>
                    </button>
                  </label>

                  <!-- Reorder & Delete -->
                  <div class="flex items-center gap-1 ml-1 sm:ml-2">
                    <button
                      type="button"
                      :disabled="index === 0"
                      class="p-1 rounded-md border border-slate-200 bg-white text-slate-500 hover:text-indigo-600 disabled:opacity-20 cursor-pointer active:scale-95"
                      @click="moveNotice(index, -1)"
                    >
                      <ArrowUpIcon class="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      :disabled="index === noticeList.length - 1"
                      class="p-1 rounded-md border border-slate-200 bg-white text-slate-500 hover:text-indigo-600 disabled:opacity-20 cursor-pointer active:scale-95"
                      @click="moveNotice(index, 1)"
                    >
                      <ArrowDownIcon class="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      class="p-1 rounded-md border border-slate-200 bg-white text-rose-500 hover:bg-rose-50 cursor-pointer active:scale-95"
                      @click="removeNotice(index)"
                    >
                      <Trash2Icon class="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              </div>

              <!-- NOTICE FORM INPUTS & MEDIA -->
              <div class="flex flex-col md:flex-row gap-4 items-start">
                <!-- IMAGE PREVIEW & UPLOADER -->
                <div class="w-full md:w-44 shrink-0 space-y-1.5">
                  <label
                    class="block text-[11px] font-semibold text-slate-600 uppercase tracking-wider"
                  >
                    Bulletin Card Image
                  </label>

                  <div
                    class="relative group rounded-xl border-2 border-dashed border-slate-300 overflow-hidden bg-white h-32 w-full flex flex-col items-center justify-center p-2 text-center transition hover:border-indigo-500"
                    style="aspect-ratio: 4 / 3"
                  >
                    <img
                      v-if="getImagePreview(notice)"
                      :src="getImagePreview(notice)"
                      alt="Notice image"
                      class="absolute inset-0 w-full h-full object-cover object-center"
                    />
                    <div
                      v-else
                      class="space-y-1 text-slate-400 p-2"
                    >
                      <ImageIcon class="mx-auto h-6 w-6" />
                      <span class="block text-[10px] font-semibold uppercase"
                        >Upload Image</span
                      >
                    </div>

                    <input
                      type="file"
                      accept="image/*"
                      class="absolute inset-0 opacity-0 cursor-pointer z-10"
                      @change="(e) => handleImageSelect(e, notice)"
                    />

                    <button
                      v-if="getImagePreview(notice)"
                      type="button"
                      class="absolute top-1.5 right-1.5 z-20 p-1 rounded-full bg-slate-900/80 text-white hover:bg-rose-600 transition"
                      @click.stop="removeImage(notice)"
                    >
                      <XIcon class="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>

                <!-- MAIN FORM FIELDS -->
                <div class="w-full flex-1 space-y-3">
                  <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div class="sm:col-span-2">
                      <label
                        class="block text-[11px] font-semibold text-slate-600 uppercase tracking-wider mb-1"
                      >
                        Notice Headline
                      </label>
                      <input
                        v-model="notice.title"
                        type="text"
                        placeholder="e.g. Annual Sports Meet Announced"
                        class="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-800 focus:border-indigo-500 focus:outline-none"
                      />
                    </div>

                    <div>
                      <label
                        class="block text-[11px] font-semibold text-slate-600 uppercase tracking-wider mb-1"
                      >
                        Category Tag
                      </label>
                      <select
                        v-model="notice.category"
                        class="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-800 focus:border-indigo-500 focus:outline-none"
                      >
                        <option value="General">General</option>
                        <option value="Admissions">Admissions</option>
                        <option value="Academics">Academics</option>
                        <option value="Events">Events</option>
                        <option value="Sports">Sports</option>
                        <option value="Holidays">Holidays</option>
                        <option value="Culture">Culture</option>
                      </select>
                    </div>

                    <div>
                      <label
                        class="block text-[11px] font-semibold text-slate-600 uppercase tracking-wider mb-1"
                      >
                        Link URL (Optional)
                      </label>
                      <input
                        v-model="notice.link"
                        type="text"
                        placeholder="https://..."
                        class="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-700 focus:border-indigo-500 focus:outline-none"
                      />
                    </div>
                  </div>

                  <div>
                    <label
                      class="block text-[11px] font-semibold text-slate-600 uppercase tracking-wider mb-1"
                    >
                      Description / Details
                    </label>
                    <input
                      v-model="notice.description"
                      type="text"
                      placeholder="Full notice description for the bulletin board..."
                      class="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-700 focus:border-indigo-500 focus:outline-none"
                    />
                  </div>

                  <!-- ICON PICKER ROW -->
                  <div class="space-y-1.5 pt-1">
                    <div class="flex items-center justify-between">
                      <label
                        class="block text-[11px] font-semibold text-slate-600 uppercase tracking-wider"
                      >
                        Notice Icon Picker
                      </label>
                      <span
                        class="text-[10px] font-mono text-indigo-600 font-bold"
                      >
                        Selected: {{ notice.icon }}
                      </span>
                    </div>

                    <div class="flex items-center gap-2">
                      <div
                        class="flex items-center gap-1.5 overflow-x-auto no-scrollbar py-1"
                      >
                        <button
                          v-for="ic in presetIcons"
                          :key="ic.class"
                          type="button"
                          class="flex items-center justify-center h-8 w-8 rounded-lg border text-xs transition cursor-pointer shrink-0"
                          :class="
                            notice.icon === ic.class
                              ? 'border-indigo-600 bg-indigo-50 text-indigo-600 font-bold'
                              : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-100'
                          "
                          :title="ic.name"
                          @click="notice.icon = ic.class"
                        >
                          <i :class="['fas', ic.class]"></i>
                        </button>
                      </div>

                      <input
                        v-model="notice.icon"
                        type="text"
                        placeholder="fa-bullhorn"
                        class="w-28 rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-xs font-mono text-slate-800 focus:border-indigo-500 focus:outline-none shrink-0"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div
              v-if="noticeList.length === 0"
              class="p-8 text-center border-2 border-dashed border-slate-200 rounded-xl"
            >
              <MegaphoneIcon class="mx-auto h-8 w-8 text-slate-400" />
              <p class="mt-2 text-xs font-medium text-slate-500">
                No announcements added yet.
              </p>
              <button
                type="button"
                class="mt-3 inline-flex items-center text-xs font-bold text-indigo-600 hover:text-indigo-500 cursor-pointer"
                @click="addNotice"
              >
                + Add First Notice
              </button>
            </div>
          </div>
        </section>
      </div>

      <!-- COMPACT FLOATING ACTION BAR (STICKY TO BOTTOM RIGHT) -->
      <div
        class="fixed bottom-4 right-3 sm:right-6 lg:right-8 z-40 max-w-[calc(100vw-1.5rem)] sm:max-w-lg"
      >
        <div
          class="rounded-2xl bg-white/95 backdrop-blur-xl border border-slate-200/90 p-2.5 sm:p-3 shadow-xl shadow-slate-900/10 flex items-center justify-between gap-3 sm:gap-4 ring-1 ring-slate-950/5"
        >
          <!-- Status Indicator (Compact) -->
          <div class="flex items-center gap-2 text-xs font-medium min-w-0">
            <!-- Saving State -->
            <span
              v-if="isSaving"
              class="inline-flex items-center gap-1.5 text-indigo-600 font-semibold truncate"
            >
              <Loader2Icon
                class="h-4 w-4 animate-spin shrink-0 text-indigo-600"
              />
              <span class="truncate">Saving...</span>
            </span>

            <!-- Unsaved Changes Badge -->
            <span
              v-else-if="hasUnsavedChanges"
              class="inline-flex items-center gap-1.5 rounded-lg bg-amber-50 px-2.5 py-1 border border-amber-200/80 text-amber-700 font-bold shrink-0"
            >
              <span
                class="h-2 w-2 rounded-full bg-amber-500 animate-pulse"
              ></span>
              <span>Unsaved</span>
            </span>

            <!-- Saved State -->
            <span
              v-else
              class="inline-flex items-center gap-1.5 text-slate-500 truncate"
            >
              <CheckCircleIcon class="h-4 w-4 text-emerald-500 shrink-0" />
              <span class="hidden sm:inline text-xs truncate"
                >All changes saved</span
              >
            </span>
          </div>

          <!-- Action Buttons -->
          <div class="flex items-center gap-2 shrink-0">
            <button
              type="button"
              class="rounded-xl border border-slate-200 bg-white px-3.5 py-2 text-xs font-bold text-slate-700 hover:bg-slate-100 hover:text-slate-900 transition active:scale-95 cursor-pointer"
              :disabled="isSaving"
              @click="confirmReset"
            >
              Discard
            </button>

            <!-- Primary Save Action Button -->
            <button
              type="button"
              class="inline-flex items-center justify-center space-x-1.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 active:scale-95 px-4 sm:px-5 py-2 text-xs font-bold text-white shadow-md shadow-indigo-600/30 transition-all duration-150 disabled:opacity-50 cursor-pointer focus:outline-none focus:ring-2 focus:ring-indigo-500/40"
              :disabled="isSaving"
              @click="handleSave"
            >
              <Loader2Icon
                v-if="isSaving"
                class="h-3.5 w-3.5 animate-spin"
              />
              <SaveIcon
                v-else
                class="h-3.5 w-3.5"
              />
              <span class="whitespace-nowrap">{{
                isSaving ? "Saving" : "Save Announcements"
              }}</span>
            </button>
          </div>
        </div>
      </div>
    </main>

    <!-- TOAST NOTIFICATION -->
    <Transition name="toast">
      <div
        v-if="toast.show"
        class="fixed bottom-24 right-4 sm:right-6 z-50 flex items-center space-x-2.5 rounded-2xl px-4 py-3 text-xs font-bold text-white shadow-2xl"
        :class="toast.type === 'success' ? 'bg-emerald-600' : 'bg-rose-600'"
      >
        <CheckCircleIcon
          v-if="toast.type === 'success'"
          class="h-4 w-4"
        />
        <AlertCircleIcon
          v-else
          class="h-4 w-4"
        />
        <span>{{ toast.message }}</span>
      </div>
    </Transition>

    <!-- RESET CONFIRMATION MODAL -->
    <div
      v-if="showResetModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-sm"
    >
      <div
        class="w-full max-w-md space-y-4 overflow-hidden rounded-2xl bg-white p-5 sm:p-6 shadow-2xl"
      >
        <h3 class="text-base font-bold text-slate-900">
          Discard Unsaved Changes?
        </h3>
        <p class="text-xs text-slate-500 leading-relaxed">
          Are you sure you want to reset all notice configurations back to their
          saved state?
        </p>
        <div class="flex justify-end space-x-2.5 pt-2">
          <button
            type="button"
            class="rounded-xl border border-slate-200 px-4 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 active:scale-95 cursor-pointer"
            @click="showResetModal = false"
          >
            Cancel
          </button>
          <button
            type="button"
            class="rounded-xl bg-rose-600 px-4 py-2 text-xs font-bold text-white shadow-md shadow-rose-600/20 hover:bg-rose-500 active:scale-95 cursor-pointer"
            @click="executeReset"
          >
            Yes, Discard
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { ref, reactive, computed, onMounted, onBeforeUnmount } from "vue";
  import { useStore } from "vuex";
  import {
    Megaphone as MegaphoneIcon,
    BellRing as BellRingIcon,
    Plus as PlusIcon,
    Save as SaveIcon,
    Loader2 as Loader2Icon,
    CheckCircle as CheckCircleIcon,
    AlertCircle as AlertCircleIcon,
    Trash2 as Trash2Icon,
    ArrowUp as ArrowUpIcon,
    ArrowDown as ArrowDownIcon,
    Pin as PinIcon,
    CheckCircle as FileTextIcon,
    Flame as FlameIcon,
    ListFilter as ListFilterIcon,
    Image as ImageIcon,
    X as XIcon,
  } from "lucide-vue-next";

  const store = useStore();

  const isLoading = ref(true);
  const isSaving = ref(false);
  const showResetModal = ref(false);

  const noticeList = ref([]);
  const initialStateJson = ref("");

  const presetIcons = [
    { name: "Bullhorn", class: "fa-bullhorn" },
    { name: "Newspaper", class: "fa-newspaper" },
    { name: "Star", class: "fa-star" },
    { name: "Trophy", class: "fa-trophy" },
    { name: "Calendar", class: "fa-calendar-day" },
    { name: "Graduation", class: "fa-graduation-cap" },
    { name: "Laptop", class: "fa-laptop" },
    { name: "Bell", class: "fa-bell" },
    { name: "Lightbulb", class: "fa-lightbulb" },
  ];

  const toast = reactive({
    show: false,
    message: "",
    type: "success",
  });

  const showToast = (message, type = "success") => {
    toast.show = true;
    toast.message = message;
    toast.type = type;
    setTimeout(() => {
      toast.show = false;
    }, 4000);
  };

  const activeNoticesCount = computed(
    () => noticeList.value.filter((n) => n.active !== false).length,
  );
  const pinnedNoticesCount = computed(
    () => noticeList.value.filter((n) => n.pinned).length,
  );
  const urgentNoticesCount = computed(
    () => noticeList.value.filter((n) => n.isUrgent).length,
  );
  const activeTickerNotices = computed(() =>
    noticeList.value.filter((n) => n.active !== false),
  );
  const pinnedBulletins = computed(() =>
    noticeList.value.filter((n) => n.pinned && n.active !== false),
  );

  function getCleanNoticeState(notice) {
    return {
      _id: notice._id,
      title: notice.title || "",
      description: notice.description || "",
      category: notice.category || "General",
      icon: notice.icon || "fa-newspaper",
      link: notice.link || "",
      isUrgent: Boolean(notice.isUrgent),
      pinned: Boolean(notice.pinned),
      active: notice.active !== false,
      image: typeof notice.image === "string" ? notice.image : "",
      hasNewFile: notice.imageFile instanceof File,
      fileName: notice.imageFile instanceof File ? notice.imageFile.name : "",
      fileSize: notice.imageFile instanceof File ? notice.imageFile.size : 0,
    };
  }

  const hasUnsavedChanges = computed(() => {
    const currentClean = noticeList.value.map(getCleanNoticeState);
    return JSON.stringify(currentClean) !== initialStateJson.value;
  });

  function snapshotState() {
    const cleanSnapshot = noticeList.value.map(getCleanNoticeState);
    initialStateJson.value = JSON.stringify(cleanSnapshot);
  }

  function addNotice() {
    noticeList.value.unshift({
      title: "",
      description: "",
      category: "General",
      icon: "fa-newspaper",
      link: "",
      image: "",
      imageFile: null,
      previewUrl: "",
      isUrgent: false,
      pinned: false,
      active: true,
    });
  }

  function removeNotice(index) {
    noticeList.value.splice(index, 1);
  }

  function moveNotice(index, direction) {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= noticeList.value.length) return;
    const item = noticeList.value.splice(index, 1)[0];
    noticeList.value.splice(targetIndex, 0, item);
  }

  function handleImageSelect(event, notice) {
    const file = event.target.files[0];
    if (!file) return;
    notice.imageFile = file;
    notice.previewUrl = URL.createObjectURL(file);
  }

  function removeImage(notice) {
    notice.image = "";
    notice.imageFile = null;
    notice.previewUrl = "";
  }

  function getImagePreview(notice) {
    return notice.previewUrl || notice.image || "";
  }

  const loadData = async () => {
    isLoading.value = true;
    try {
      await store.dispatch("fetchWebsiteDetails");
      const details = store.state.websiteDetails || {};

      if (Array.isArray(details.news) && details.news.length > 0) {
        noticeList.value = details.news.map((n) => ({
          _id: n._id,
          title: n.title || "",
          description: n.description || "",
          category: n.category || "General",
          icon: n.icon || "fa-newspaper",
          image: n.image || "",
          imageFile: null,
          previewUrl: "",
          link: n.link || "",
          isUrgent: Boolean(n.isUrgent),
          pinned: Boolean(n.pinned),
          active: n.active !== false,
        }));
      } else {
        noticeList.value = [
          {
            title: "Admission open for 2026–27 — apply online now",
            description:
              "Online applications are now open for Nursery through Grade XI.",
            category: "Admissions",
            icon: "fa-bullhorn",
            image:
              "https://dpstezpur.com/wp-content/uploads/2026/05/Best-Out-Of-Waste-Activity-6-1940x1455.jpeg",
            imageFile: null,
            previewUrl: "",
            link: "/admissions",
            isUrgent: true,
            pinned: true,
            active: true,
          },
        ];
      }

      snapshotState();
    } catch (err) {
      showToast("Failed to load notice details", "error");
    } finally {
      isLoading.value = false;
    }
  };

  const handleSave = async () => {
    isSaving.value = true;
    try {
      const payload = {
        news: noticeList.value.map((n) => ({
          _id: n._id,
          title: n.title || "",
          description: n.description || "",
          category: n.category || "General",
          icon: n.icon || "fa-newspaper",
          link: n.link || "",
          pinned: Boolean(n.pinned),
          active: n.active !== false,
          image: typeof n.image === "string" ? n.image : "",
          imageFile: n.imageFile || null,
        })),
      };

      await store.dispatch("updateWebsiteDetails", payload);
      await loadData();
      showToast("Announcements saved successfully!", "success");
    } catch (err) {
      showToast(err.message || "Error saving notices", "error");
    } finally {
      isSaving.value = false;
    }
  };

  const confirmReset = () => {
    if (hasUnsavedChanges.value) {
      showResetModal.value = true;
    } else {
      executeReset();
    }
  };

  const executeReset = () => {
    showResetModal.value = false;
    loadData();
    showToast("Notice board reset to last saved state", "success");
  };

  const handleKeyDown = (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === "s") {
      e.preventDefault();
      handleSave();
    }
  };

  onMounted(async () => {
    await loadData();
    window.addEventListener("keydown", handleKeyDown);
  });

  onBeforeUnmount(() => {
    window.removeEventListener("keydown", handleKeyDown);
  });
</script>

<style scoped>
  .toast-enter-active,
  .toast-leave-active {
    transition: all 0.3s ease;
  }
  .toast-enter-from,
  .toast-leave-to {
    opacity: 0;
    transform: translateY(1rem);
  }

  .no-scrollbar::-webkit-scrollbar {
    display: none;
  }
  .no-scrollbar {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }

  .line-clamp-2 {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
</style>
